import { css } from '@emotion/react';
import { useActor } from '@xstate/react';
import { FC, ReactElement, ReactNode, SyntheticEvent, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { AddressFormFeatures } from 'api/api';

import { Icon } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { ArrowTopLeftRounded } from '~src/icons/arrow-top-left-rounded';
import { colors } from '~src/theme/colors';
import { fontStyles } from '~src/theme/fontStyles';
import { Button } from '~src/ui/button';

import { useAddressFormBusinessLogic } from '~address-form/context/use-address-form';
import { formatAddressToTokens } from '~address-form/utils/format';

import { ExtendedValidationHandler } from '../state-machines/shared-types';
import { COUNTRIES_WITH_EXTENDED_REGION_VALIDATION } from '../state-machines/suggestion-service';
import { MetaAddressFormFields } from '../types';
import { formatSuggestionToHTML } from '../utils/format-ui';
import { processSuggestion } from '../validation/suggestion-validation';

const INCORRECT_ADDRESS_PREDICATES_LOQATE = ['ADDRESS_LINES'] as const;
const INCORRECT_ADDRESS_PREDICATES_GOOGLE = ['STREET', 'STREET_NUMBER', 'ADDRESS_LINES'] as const;

const getIncorrectAddressPredicates = (provider: AddressFormFeatures['suggestion_provider']) => {
  if (provider === 'loqate') return INCORRECT_ADDRESS_PREDICATES_LOQATE;

  return INCORRECT_ADDRESS_PREDICATES_GOOGLE;
};

/**
 * This component either:
 * *) displays address suggestion/hints related to incorrect address
 * *) if enable_extended_validation is turned on - delegates the information about current suggestion
 * state to current flow's state machine
 */

interface AddressSuggestionControllerProps {
  triggerExtendedValidation?: ExtendedValidationHandler;
}

export const AddressSuggestionController: FC<AddressSuggestionControllerProps> = ({
  triggerExtendedValidation,
}) => {
  const { tICU } = useTranslations();
  const globalState = useGlobalState();
  const formInstance = useFormContext<MetaAddressFormFields>();
  const { addressService, submitHandler, addressFormValidationConfig, addressFormFeatures } =
    useAddressFormBusinessLogic();

  const [parentState, sendParentState] = useActor(addressService);
  const [suggestionState] = useActor(parentState.context.suggestion);
  const [streetNumberState] = useActor(parentState.context.streetNumber);

  const addressNeedsUserConfirmation =
    ['needs_confirmation', 'ready.by_autocomplete'].some(suggestionState.matches) &&
    streetNumberState.matches('ready.by_user_confirmation');

  const isLackingAddressLineData =
    (addressNeedsUserConfirmation && !suggestionState.context.validation?.suggestion?.address) ||
    (addressNeedsUserConfirmation && // loqate case when everything is correct apart from address line - we need to test this extensively
      suggestionState.context.validation?.suggestion?.invalid_components?.length === 1 &&
      suggestionState.context.validation?.suggestion?.invalid_components?.includes(
        'ADDRESS_LINES'
      ));

  const isSuggestionValid = useMemo(
    () =>
      processSuggestion(
        formInstance.getValues(),
        suggestionState.context.validation?.suggestion?.address,
        addressFormValidationConfig
      ),
    [suggestionState.context.validation?.suggestion?.address]
  );

  const formattedAddress = useMemo(
    () =>
      formatAddressToTokens(formInstance.getValues(), {
        country: globalState.purchase_country,
        show_street: true,
        show_city: true,
        show_postal_code: true,
        show_region: true,
        presentational: true,
      }),
    [parentState.matches('validating')]
  );

  useEffect(() => {
    if (!globalState.address_form_configuration?.global_features?.enable_extended_validation) {
      return;
    }

    if (isLackingAddressLineData) {
      const isSuggestionMissingPredicateAddressComponents = getIncorrectAddressPredicates(
        addressFormFeatures.suggestion_provider
      ).every(component =>
        suggestionState.context.validation?.suggestion?.invalid_components?.includes(component)
      );

      if (isSuggestionMissingPredicateAddressComponents) {
        formInstance.setError('address_lines.0', {
          type: 'warning',
          message: tICU('widget.validation.INVALID_ADDRESS_LINE'),
        });
      } else if (
        suggestionState.matches('needs_confirmation') &&
        !suggestionState.context.validation?.suggestion?.address &&
        COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(globalState.purchase_country)
      ) {
        if (
          suggestionState.context.validation?.suggestion?.invalid_components?.includes(
            'POSTAL_CODE'
          )
        ) {
          formInstance.setError('postal_code', {
            type: 'address_mismatch',
            message: tICU('widget.error_modal.INVALID_ADDRESS.TITLE'),
          });
          /**
           * The state reset here is a bit bruteforcey - leading to
           * suggestion and streetnumber actors to be reinitialised.
           * If there are any suboptimisations needed modifying the event
           * sent here is the way to go
           */
          sendParentState({ type: 'RESET_STATE' });
        } else {
          formInstance.setError('region', {
            type: 'address_mismatch',
            message: `${tICU('widget.validation.SELECT_VALID_STATE')} ${formInstance.getValues('postal_code')}`,
          });
        }
      } else {
        formInstance.setError('address_lines.0', {
          type: 'warning',
          message: tICU('widget.validation.MISSING_STREET_NUMBER'),
        });
      }
    } else if (isSuggestionValid) {
      // enable_extended_validation is checked at the start of useEffect
      const isSubmissionBlocking =
        COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(globalState.purchase_country) &&
        /**
         *  temporary solution for submission blockage - it will be adjusted after
         *  the talks with designers about suggestion -> invalid postal_code path
         */
        ['REGION', 'POSTAL_CODE'].some(token =>
          suggestionState.context.validation?.suggestion?.invalid_components?.includes(token)
        );
      if (isSubmissionBlocking) {
        formInstance.setError('region', {
          type: 'address_mismatch',
          message: `${tICU('widget.validation.SELECT_VALID_STATE')} ${formInstance.getValues('postal_code')}`,
        });
      }

      triggerExtendedValidation?.({
        address: { ...formInstance.getValues() },
        suggestion: { ...suggestionState.context.validation!.suggestion },
        isSubmissionBlocking: !!isSubmissionBlocking,
      });
    }
  }, [
    globalState.address_form_configuration?.global_features?.enable_extended_validation,
    isSuggestionValid,
    isLackingAddressLineData,
    suggestionState,
  ]);

  if (globalState.address_form_configuration?.global_features?.enable_extended_validation) {
    /**
     * don't show the pop-up message - with enable_extended_validation flag we are
     * handling suggestions in external modal
     */

    return null;
  }

  if (suggestionState.matches('unverified') || streetNumberState.matches('unverified')) {
    return null;
  }

  if (isLackingAddressLineData) {
    const isSuggestionMissingPredicateAddressComponents = getIncorrectAddressPredicates(
      addressFormFeatures.suggestion_provider
    ).every(component =>
      suggestionState.context.validation?.suggestion?.invalid_components?.includes(component)
    );

    const address = formattedAddress
      .map(token => (typeof token === 'string' ? token : token.value))
      .join('');

    return isSuggestionMissingPredicateAddressComponents ? (
      <AddressSuggestionButtonLayout
        header={tICU('widget.delivery_address.SUGGESTION.ADDRESS_INCORRECT_HEADER')}
        description={tICU('widget.delivery_address.SUGGESTION.ADDRESS_INCORRECT_DESCRIPTION', {
          address,
        })}
        buttonText={tICU('widget.delivery_address.EDIT_MODAL.TITLE')}
        icon={<ArrowTopLeftRounded />}
        onClick={e => {
          e.preventDefault();
          formInstance.setFocus('address_lines.0');
        }}
      />
    ) : (
      <AddressSuggestionButtonLayout
        header={tICU('widget.delivery_address.SUGGESTION.STREET_NUMBER_HEADER')}
        description={tICU('widget.delivery_address.SUGGESTION.STREET_NUMBER_DESCRIPTION', {
          address,
        })}
        buttonText={tICU('widget.delivery_address.SUGGESTION.ADD_BTN')}
        icon={<Icon type="plussign" color="black" />}
        onClick={e => {
          e.preventDefault();
          formInstance.setFocus('address_lines.0');
        }}
      />
    );
  }

  if (!suggestionState.context.validation?.suggestion?.address) return null;

  if (isSuggestionValid) {
    const suggestedAddress = formatSuggestionToHTML(
      suggestionState.context.validation.suggestion,
      globalState.purchase_country
    );

    return (
      <AddressSuggestionButtonLayout
        header={tICU('widget.delivery_address.SUGGESTION.HEADER')}
        description={suggestedAddress}
        buttonText={tICU('widget.delivery_address.SUGGESTION.SUBMIT_BTN')}
        icon={<ArrowTopLeftRounded />}
        onClick={e => {
          // Preventing the default here is important to stop double form submissions
          e.preventDefault();
          submitHandler({
            ...formInstance.getValues(),
            ...suggestionState.context.validation!.suggestion!.address,
          });
        }}
      />
    );
  }

  const address = formattedAddress
    .map(token => (typeof token === 'string' ? token : token.value))
    .join('');

  return (
    <AddressSuggestionButtonLayout
      header={tICU('widget.delivery_address.SUGGESTION.ADDRESS_INCORRECT_HEADER')}
      description={tICU('widget.delivery_address.SUGGESTION.ADDRESS_INCORRECT_DESCRIPTION', {
        address,
      })}
      buttonText={tICU('widget.delivery_address.EDIT_MODAL.TITLE')}
      icon={<ArrowTopLeftRounded />}
      onClick={e => {
        e.preventDefault();
        formInstance.setFocus('address_lines.0');
      }}
    />
  );
};

interface AddressSuggestionButtonLayoutProps {
  header: string;
  description: string | ReactNode[];
  buttonText: string;
  icon: ReactElement;
  onClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
}

const AddressSuggestionButtonLayout: FC<AddressSuggestionButtonLayoutProps> = ({
  header,
  description,
  buttonText,
  icon,
  onClick,
}) => {
  return (
    <div css={styles.container}>
      <div css={styles.header}>{header}</div>
      <div css={styles.addressWrapper}>{description}</div>
      <div css={styles.buttonWrapper}>
        <Button
          buttonType="secondary"
          type="button"
          css={styles.submitButton}
          data-test="suggest-button"
          onClick={onClick}
        >
          {icon}
          <span css={styles.submitButtonText}>{buttonText}</span>
        </Button>
      </div>
    </div>
  );
};

const styles = {
  container: css({
    backgroundColor: colors.superlightOrange,
    borderRadius: '2px',
    padding: '12px 16px',
    marginTop: '16px',
    ...fontStyles.px14_20,
  }),
  header: {
    fontVariationSettings: '"wght" 500',
    marginBottom: '2px',
  },
  addressWrapper: {
    marginBottom: '16px',
  },
  buttonWrapper: {
    display: 'flex',
  },
  submitButton: {
    backgroundColor: 'transparent',
    height: '32px',
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
  },
  submitButtonText: {
    marginLeft: '5px',
  },
};
